import { Component, Input, OnChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AgremoStatistics } from '../../../core/response-models/agremo-statistics.model';
import { AgremoStatisticsGeneral } from '../../../core/response-models/agremo-statistics-general.model';
import { AgremoStatisticsCluster } from '../../../core/response-models/agremo-statistics-cluster.model';

@Component({
  selector: 'app-agremo-statistics',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './agremo-statistics.component.html',
  styleUrl: './agremo-statistics.component.scss',
})
export class AgremoStatisticsComponent implements OnChanges {
  @Input() statistics: AgremoStatistics | null | undefined = null;

  general: AgremoStatisticsGeneral | null = null;
  clusters: AgremoStatisticsCluster[] = [];
  pieGradient = '';
  dominantClusterColor = '';
  remainingEntries: { key: string; value: string }[] = [];

  ngOnChanges(): void {
    this.computeData();
  }

  private computeData(): void {
    if (!this.statistics) {
      this.general = null;
      this.clusters = [];
      this.pieGradient = '';
      this.dominantClusterColor = '';
      this.remainingEntries = [];
      return;
    }

    // Extract general
    this.general = this.statistics.general || null;

    // Extract and sort clusters by percentage descending
    const rawClusters = this.statistics.clusters;
    this.clusters = Array.isArray(rawClusters)
      ? [...rawClusters].sort((a, b) => b.percentage - a.percentage)
      : [];

    // Build pie gradient
    this.pieGradient = this.buildPieGradient();

    // Dominant cluster color (largest percentage)
    this.dominantClusterColor = this.clusters.length > 0
      ? `#${this.clusters[0].color}`
      : '';

    // Remaining entries (anything not general/clusters)
    this.remainingEntries = Object.entries(this.statistics)
      .filter(([key]) => key !== 'general' && key !== 'clusters')
      .map(([key, value]) => ({
        key: this.formatKey(key),
        value: typeof value === 'object' && value !== null
          ? JSON.stringify(value, null, 2)
          : String(value ?? '-'),
      }));
  }

  private buildPieGradient(): string {
    if (this.clusters.length === 0) return 'var(--bg-tertiary)';

    const stops: string[] = [];
    let cumulative = 0;

    for (let i = 0; i < this.clusters.length; i++) {
      const cluster = this.clusters[i];
      const color = `#${cluster.color}`;
      const start = cumulative;
      const end = i === this.clusters.length - 1
        ? 100
        : Math.round((cumulative + cluster.percentage) * 100) / 100;
      stops.push(`${color} ${start}% ${end}%`);
      cumulative = end;
    }

    return `conic-gradient(${stops.join(', ')})`;
  }

  private formatKey(key: string): string {
    return key
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .replace(/^\w/, (c) => c.toUpperCase())
      .trim();
  }
}
